/* Custom testimonials — carousel + grid custom elements */
(function () {
  if (!customElements.get('testimonial-carousel')) {
    class TestimonialCarousel extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-track]');
        if (!this.track) return;

        this.prevBtn = this.querySelector('.custom-testimonials__arrow--prev');
        this.nextBtn = this.querySelector('.custom-testimonials__arrow--next');

        this.perViewDesktop = parseInt(this.dataset.perViewDesktop, 10) || 3;
        this.track.style.setProperty('--t-per-view', String(this.perViewDesktop));

        this.originals = Array.from(this.track.children);
        this.realCount = this.originals.length;
        this.cloneCount = Math.min(this.perViewDesktop, this.realCount);

        // With only one card there's nothing to loop through.
        if (this.realCount > 1) {
          this._addClones();
          requestAnimationFrame(() => {
            this._setInitialPosition();
            this._setupScrollLoopWatcher();
          });
        }

        this.prevBtn?.addEventListener('click', () => this._goByPage(-1));
        this.nextBtn?.addEventListener('click', () => this._goByPage(1));

        this._setupDrag();
        window.addEventListener('resize', () => this._handleResize(), { passive: true });
      }

      // Standard infinite-carousel pattern: clone the first N cards to the end
      // and the last N cards to the start. We then position the scroll past
      // the prepended tail clones so the user starts on the first real card.
      // When they scroll into a clone region we silently teleport them to the
      // equivalent real-card position — so swiping past the last card lands
      // on the first card again, and swiping before the first card lands on
      // the last, without any visible jump or rewind animation.
      _addClones() {
        const headClones = this.originals.slice(0, this.cloneCount).map((c) => {
          const clone = c.cloneNode(true);
          clone.setAttribute('data-clone', 'head');
          clone.setAttribute('aria-hidden', 'true');
          return clone;
        });
        const tailClones = this.originals.slice(-this.cloneCount).map((c) => {
          const clone = c.cloneNode(true);
          clone.setAttribute('data-clone', 'tail');
          clone.setAttribute('aria-hidden', 'true');
          return clone;
        });

        // Tail clones go at the start (in original order so the visual
        // sequence reading right-to-left is N-2, N-1, N before the first
        // real card). Head clones go at the end.
        tailClones.forEach((c) => this.track.appendChild(c)); // temp append
        // Now move tail clones to the very start.
        tailClones.forEach((c) => this.track.insertBefore(c, this.track.firstChild));
        // Then head clones at the end.
        headClones.forEach((c) => this.track.appendChild(c));
      }

      _firstRealCard() {
        return this.track.children[this.cloneCount] || null;
      }

      _lastRealCard() {
        return this.track.children[this.cloneCount + this.realCount - 1] || null;
      }

      _firstHeadClone() {
        return this.track.children[this.cloneCount + this.realCount] || null;
      }

      _setInitialPosition() {
        const firstReal = this._firstRealCard();
        if (!firstReal) return;
        // Use scroll-behavior auto for an instant jump on init.
        this._jumpTo(firstReal.offsetLeft - this.track.offsetLeft);
      }

      _jumpTo(left) {
        const prev = this.track.style.scrollBehavior;
        this.track.style.scrollBehavior = 'auto';
        this.track.scrollLeft = left;
        // Force a reflow to commit the position before restoring.
        // eslint-disable-next-line no-unused-expressions
        this.track.scrollLeft;
        this.track.style.scrollBehavior = prev || '';
      }

      _setupScrollLoopWatcher() {
        let scrollTimer;
        const schedule = (delay) => {
          clearTimeout(scrollTimer);
          scrollTimer = setTimeout(() => this._maybeWrap(), delay);
        };
        // Scroll-settle path: each scroll event resets a timer; once the
        // user stops scrolling we check for wrap.
        this.track.addEventListener('scroll', () => schedule(120), { passive: true });
        // Touchend backup: iOS Safari momentum scrolling can keep firing
        // late scroll events after a programmatic teleport, starving the
        // settle timer and making the second loop pass feel stuck. Touchend
        // fires reliably when the user lifts their finger, so we schedule
        // a separate wrap check that the scroll-settle path can't block.
        this.track.addEventListener(
          'touchend',
          () => schedule(180),
          { passive: true }
        );
      }

      _maybeWrap() {
        const firstReal = this._firstRealCard();
        const firstHeadClone = this._firstHeadClone();
        if (!firstReal || !firstHeadClone) return;

        const realStart = firstReal.offsetLeft - this.track.offsetLeft;
        const headStart = firstHeadClone.offsetLeft - this.track.offsetLeft;
        const scroll = this.track.scrollLeft;

        if (scroll >= headStart - 1) {
          // Scrolled into the head clones (past the real end) — teleport
          // back to the equivalent real-card position. User keeps the
          // illusion that they're scrolling forward into the first card.
          const offset = scroll - headStart;
          this._jumpTo(realStart + offset);
        } else if (scroll <= realStart - this._firstTailCloneWidth() + 1) {
          // Scrolled into the tail clones (before the real start) —
          // teleport forward to the equivalent real-card position.
          const realEnd = this._lastRealCard().offsetLeft - this.track.offsetLeft + this._lastRealCard().offsetWidth;
          const distance = realStart - scroll;
          this._jumpTo(realEnd - distance);
        }
      }

      _firstTailCloneWidth() {
        // The total horizontal span of the prepended tail clones.
        const last = this.track.children[this.cloneCount - 1];
        if (!last) return 0;
        return last.offsetLeft - this.track.offsetLeft + last.offsetWidth;
      }

      _goByPage(direction) {
        const firstReal = this._firstRealCard();
        if (!firstReal) return;
        const perView = this._currentPerView();
        const styles = getComputedStyle(this.track);
        const gap = parseInt(styles.columnGap || styles.gap, 10) || 0;
        const cardWidth = firstReal.getBoundingClientRect().width;
        const distance = (cardWidth + gap) * perView * direction;

        // Smooth scrollBy by one page; the scroll-loop watcher above
        // teleports past the clone region when the scroll settles, so
        // there's no rewind animation across all the cards.
        this.track.scrollBy({ left: distance, behavior: 'smooth' });
      }

      _currentPerView() {
        if (window.matchMedia('(min-width: 990px)').matches) {
          return this.perViewDesktop;
        }
        return 1;
      }

      _handleResize() {
        // On viewport change the per-view may flip between desktop / mobile,
        // which changes card widths. Re-anchor to the closest real card so
        // the user keeps their place.
        const firstReal = this._firstRealCard();
        if (!firstReal) return;
        const realStart = firstReal.offsetLeft - this.track.offsetLeft;
        if (this.track.scrollLeft < realStart) this._jumpTo(realStart);
      }

      _setupDrag() {
        let isDown = false;
        let startX = 0;
        let scrollLeft = 0;
        let moved = false;

        const onDown = (e) => {
          if (e.type === 'mousedown' && e.button !== 0) return;
          isDown = true;
          moved = false;
          startX = e.pageX - this.track.getBoundingClientRect().left;
          scrollLeft = this.track.scrollLeft;
          this.track.classList.add('is-dragging');
        };

        const onMove = (e) => {
          if (!isDown) return;
          const x = e.pageX - this.track.getBoundingClientRect().left;
          const walk = x - startX;
          if (Math.abs(walk) > 4) moved = true;
          this.track.scrollLeft = scrollLeft - walk;
        };

        const onUp = () => {
          if (!isDown) return;
          isDown = false;
          this.track.classList.remove('is-dragging');
        };

        this.track.addEventListener(
          'click',
          (e) => {
            if (moved) {
              e.preventDefault();
              e.stopPropagation();
              moved = false;
            }
          },
          true
        );

        this.track.addEventListener('mousedown', onDown);
        this.track.addEventListener('mousemove', onMove);
        this.track.addEventListener('mouseup', onUp);
        this.track.addEventListener('mouseleave', onUp);
      }
    }
    customElements.define('testimonial-carousel', TestimonialCarousel);
  }

  if (!customElements.get('testimonial-grid')) {
    class TestimonialGrid extends HTMLElement {
      connectedCallback() {
        this.initial = parseInt(this.dataset.initial, 10) || 3;
        this.step = parseInt(this.dataset.step, 10) || 3;
        this.cards = Array.from(this.children);
        this.btn = this.parentElement?.querySelector('[data-show-more]');

        this.visible = this.initial;
        this._apply();

        this.btn?.addEventListener('click', () => this._toggle());
      }

      _apply() {
        this.cards.forEach((card, i) => {
          if (i < this.visible) {
            card.removeAttribute('data-hidden');
          } else {
            card.setAttribute('data-hidden', '');
          }
        });

        if (this.btn) {
          const allShown = this.visible >= this.cards.length;
          this.btn.textContent = allShown
            ? this.btn.dataset.lessText || 'Hide'
            : this.btn.dataset.moreText || 'Show more';
        }
      }

      _toggle() {
        if (this.visible >= this.cards.length) {
          this.visible = this.initial;
        } else {
          this.visible = Math.min(this.visible + this.step, this.cards.length);
        }
        this._apply();
      }
    }
    customElements.define('testimonial-grid', TestimonialGrid);
  }
})();
